import { Transaction } from '../types';
import { getDefaultTrackingWindowHours } from '../config';

/**
 * Comprehensive time calculation utilities for VeriPay
 * Provides centralized, robust time parsing and formatting
 * Eliminates NaN errors and ensures temporal consistency
 */

// Types for better type safety
export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds?: number;
  totalHours: number;
  isExpired: boolean;
  isValid: boolean;
}

export interface TimeDisplayOptions {
  includeSeconds?: boolean;
  shortFormat?: boolean;
  showExpiredText?: boolean;
  expiredText?: string;
}

/**
 * Safely parse .NET TimeSpan string or number to hours
 * Handles all edge cases and prevents NaN errors
 */
export const safeParseTimeSpanToHours = (timeSpan: string | number | null | undefined): number => {
  // Handle null/undefined cases
  if (timeSpan === null || timeSpan === undefined) {
    console.warn('TimeCalculations: Received null/undefined timeSpan, returning 0');
    return 0;
  }

  // If it's already a number, validate it
  if (typeof timeSpan === 'number') {
    if (isNaN(timeSpan) || !isFinite(timeSpan)) {
      console.warn('TimeCalculations: Received invalid number timeSpan:', timeSpan);
      return 0;
    }
    return Math.max(0, timeSpan); // Ensure non-negative
  }

  // If it's not a string, convert to string first
  if (typeof timeSpan !== 'string') {
    console.warn('TimeCalculations: Received non-string, non-number timeSpan:', typeof timeSpan, timeSpan);
    return 0;
  }

  // Handle empty string
  if (!timeSpan.trim()) {
    console.warn('TimeCalculations: Received empty timeSpan string');
    return 0;
  }

  try {
    // Parse the TimeSpan string (format: "hh:mm:ss.fffffff" or variations)
    const parts = timeSpan.trim().split(':');
    
    if (parts.length < 2) {
      console.warn('TimeCalculations: Invalid TimeSpan format (too few parts):', timeSpan);
      return 0;
    }

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    // Validate parsed values
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('TimeCalculations: Invalid TimeSpan format (NaN in hours/minutes):', timeSpan);
      return 0;
    }

    // Handle seconds and milliseconds if present
    let seconds = 0;
    let milliseconds = 0;

    if (parts.length > 2) {
      const secondsParts = parts[2].split('.');
      seconds = parseInt(secondsParts[0], 10);
      
      if (isNaN(seconds)) {
        console.warn('TimeCalculations: Invalid seconds in TimeSpan:', timeSpan);
        seconds = 0;
      }

      if (secondsParts.length > 1) {
        // Convert fractional seconds to milliseconds
        const fractionalStr = secondsParts[1].substring(0, 3).padEnd(3, '0');
        milliseconds = parseInt(fractionalStr, 10);
        
        if (isNaN(milliseconds)) {
          milliseconds = 0;
        }
      }
    }

    // Convert everything to hours
    const totalHours = hours + (minutes / 60) + (seconds / 3600) + (milliseconds / 3600000);
    
    // Ensure non-negative result
    const result = Math.max(0, totalHours);
    
    console.log(`TimeCalculations: Parsed TimeSpan "${timeSpan}" to ${result} hours`);
    return result;
  } catch (error) {
    console.error('TimeCalculations: Error parsing TimeSpan:', error, 'Input:', timeSpan);
    return 0;
  }
};

/**
 * Calculate remaining time for tracking submission
 * Handles both backend-provided and frontend-calculated values
 */
export const getTrackingTimeRemaining = (transaction: Transaction): TimeRemaining => {
  const defaultResult: TimeRemaining = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalHours: 0,
    isExpired: true,
    isValid: false
  };

  // Validate transaction input
  if (!transaction || typeof transaction !== 'object') {
    console.warn('TimeCalculations: Invalid transaction object provided');
    return defaultResult;
  }

  // Check if backend has already calculated expiration
  if (transaction.isTrackingExpired === true) {
    console.log('TimeCalculations: Backend indicates tracking is expired');
    return defaultResult;
  }

  // Try to use backend-provided remaining hours first
  if (transaction.remainingHoursForTracking !== undefined && transaction.remainingHoursForTracking !== null) {
    const totalHours = safeParseTimeSpanToHours(transaction.remainingHoursForTracking);
    
    if (totalHours <= 0) {
      return defaultResult;
    }

    const days = Math.floor(totalHours / 24);
    const hours = Math.floor(totalHours % 24);
    const minutes = Math.round((totalHours % 1) * 60);
    const seconds = Math.round(((totalHours % 1) * 60 % 1) * 60);

    return {
      days,
      hours,
      minutes,
      seconds,
      totalHours,
      isExpired: false,
      isValid: true
    };
  }

  // Fallback to frontend calculation
  if (!transaction.createdAt) {
    console.warn('TimeCalculations: No createdAt timestamp available for frontend calculation');
    return defaultResult;
  }

  try {
    const createdAt = new Date(transaction.createdAt);
    const hoursToProvideTracking = getDefaultTrackingWindowHours();
    
    // Validate dates
    if (isNaN(createdAt.getTime())) {
      console.warn('TimeCalculations: Invalid createdAt timestamp:', transaction.createdAt);
      return defaultResult;
    }

    const deadline = new Date(createdAt);
    deadline.setHours(deadline.getHours() + hoursToProvideTracking);

    const now = new Date();
    const diffMs = deadline.getTime() - now.getTime();

    if (diffMs <= 0) {
      return defaultResult;
    }

    const totalHours = diffMs / (1000 * 60 * 60);
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      totalHours,
      isExpired: false,
      isValid: true
    };
  } catch (error) {
    console.error('TimeCalculations: Error in frontend time calculation:', error);
    return defaultResult;
  }
};

/**
 * Check if tracking window has expired
 */
export const isTrackingExpired = (transaction: Transaction): boolean => {
  // Use backend value if available
  if (transaction.isTrackingExpired !== undefined) {
    return transaction.isTrackingExpired;
  }

  // Fallback to calculation
  const timeRemaining = getTrackingTimeRemaining(transaction);
  return timeRemaining.isExpired;
};

/**
 * Format time display with various options
 */
export const formatTimeDisplay = (timeRemaining: TimeRemaining, options: TimeDisplayOptions = {}): string => {
  const {
    includeSeconds = false,
    shortFormat = true,
    showExpiredText = true,
    expiredText = 'Expired'
  } = options;

  if (!timeRemaining.isValid || timeRemaining.isExpired) {
    return showExpiredText ? expiredText : '';
  }

  const { days, hours, minutes, seconds } = timeRemaining;

  if (days > 0) {
    return shortFormat 
      ? `${days}d ${hours}h`
      : `${days} ${days === 1 ? 'day' : 'days'} ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  } else if (hours > 0) {
    const base = shortFormat ? `${hours}h ${minutes}m` : `${hours}:${minutes.toString().padStart(2, '0')}`;
    return includeSeconds && seconds !== undefined ? `${base} ${seconds}s` : base;
  } else {
    const base = shortFormat ? `${minutes}m` : `${minutes}:${(seconds || 0).toString().padStart(2, '0')}`;
    return includeSeconds && seconds !== undefined ? `${minutes}m ${seconds}s` : base;
  }
};

/**
 * Get color class based on remaining time
 */
export const getTimeColorClass = (timeRemaining: TimeRemaining): string => {
  if (!timeRemaining.isValid || timeRemaining.isExpired) {
    return 'time-expired';
  }

  const totalHours = timeRemaining.totalHours;
  
  if (totalHours < 4) {
    return 'time-critical';
  } else if (totalHours < 12) {
    return 'time-warning';
  } else {
    return 'time-normal';
  }
};

/**
 * Comprehensive tracking status information
 */
export interface TrackingStatus {
  timeRemaining: TimeRemaining;
  colorClass: string;
  displayText: string;
  isExpired: boolean;
  needsTracking: boolean;
}

/**
 * Get complete tracking status for a transaction
 */
export const getTrackingStatus = (transaction: Transaction, options: TimeDisplayOptions = {}): TrackingStatus => {
  const timeRemaining = getTrackingTimeRemaining(transaction);
  const colorClass = getTimeColorClass(timeRemaining);
  const displayText = formatTimeDisplay(timeRemaining, options);
  const isExpired = timeRemaining.isExpired;
  const needsTracking = transaction.status === 'in_escrow' && !transaction.trackingId && !isExpired;

  return {
    timeRemaining,
    colorClass,
    displayText,
    isExpired,
    needsTracking
  };
};

/**
 * Legacy compatibility function - maps to new centralized function
 * @deprecated Use getTrackingTimeRemaining instead
 */
export const getRemainingTime = (transaction: Transaction): { days: number, hours: number, minutes: number } | null => {
  console.warn('TimeCalculations: getRemainingTime is deprecated, use getTrackingTimeRemaining instead');
  const result = getTrackingTimeRemaining(transaction);
  
  if (!result.isValid || result.isExpired) {
    return null;
  }
  
  return {
    days: result.days,
    hours: result.hours,
    minutes: result.minutes
  };
};

/**
 * Legacy compatibility function - maps to new centralized function
 * @deprecated Use isTrackingExpired instead
 */
export const isWithinTimeWindow = (transaction: Transaction): boolean => {
  console.warn('TimeCalculations: isWithinTimeWindow is deprecated, use isTrackingExpired instead');
  return !isTrackingExpired(transaction);
};

/**
 * Legacy compatibility function - maps to new centralized function
 * @deprecated Use formatTimeDisplay instead
 */
export const formatRemainingTime = (days: number, hours: number, minutes: number): string => {
  console.warn('TimeCalculations: formatRemainingTime is deprecated, use formatTimeDisplay instead');
  const timeRemaining: TimeRemaining = {
    days,
    hours,
    minutes,
    seconds: 0,
    totalHours: days * 24 + hours + minutes / 60,
    isExpired: false,
    isValid: true
  };
  
  return formatTimeDisplay(timeRemaining, { shortFormat: true });
};